import { createFileRoute, useRouter } from "@tanstack/react-router";
import Left from "@repo/icons/default/Left";
import { graphql } from "@repo/graphql-types";
import { z } from "zod";
import { useResetAtom } from "jotai/utils";
import { useAtomValue } from "jotai";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { useGraphQL } from "@/hooks/use-graphql";
import { BankAccountCreateForm } from "@/components/payment/bank/account/bank-account-create-form";
import { PendingComponent } from "@/components/ui/loading/pending-component";
import { confirmDocumentAtom } from "@/lib/atoms/confirm-document-atom";

import type { JSX } from "react";

const AddBankAccountPageQuery = graphql(/* GraphQL */ `
  query AddBankAccountPageQuery($codUsuario: Int!, $codClinica: Int, $skipClinica: Boolean!) {
    usuarios(where: { codUsuario: { _eq: $codUsuario } }) {
      cpf
      nome
      sobrenome
    }
    clinicas: tbClinicas(where: { codClinica: { _eq: $codClinica } }) @skip(if: $skipClinica) {
      cnpj
      razaoSocial
    }
    ...BankAccountCreateFormQueryFragment
  }
`);

const addBankAccountPageSearchParamsSchema = z.object({
  action: z.string().optional(),
});

const AddBankAccountPage = (): JSX.Element => {
  const { user } = Route.useRouteContext();
  const router = useRouter();
  const resetConfirmDocumentAtom = useResetAtom(confirmDocumentAtom);
  const {
    newClinicaId,
    cnpj: newClinicaCnpj,
    nomeEmpresa: newClinicaName,
  } = useAtomValue(confirmDocumentAtom);

  const queryResult = useGraphQL(AddBankAccountPageQuery, {
    codUsuario: user.codUsuario,
    codClinica: user.codClinica ?? undefined,
    skipClinica: !user.codClinica,
  });
  const { data } = queryResult;

  const usuario = data?.usuarios[0];
  const clinica = data?.clinicas?.[0];

  // Se temos dados de clínica recém-criada via atom, usar esses dados
  const clinicaData = newClinicaId
    ? {
        cnpj: newClinicaCnpj,
        razaoSocial: newClinicaName,
      }
    : clinica;

  // Se veio do fluxo de criação de clínica (PF→PJ), voltar deve ir para /payments
  const handleBack = (): void => {
    if (newClinicaId) {
      resetConfirmDocumentAtom();
      router.history.go(-4);
    } else {
      router.history.back();
    }
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" onClick={handleBack} />
        <HeaderTitle title="Adicionar conta" align="center" />
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper {...queryResult}>
          {data ? (
            <BankAccountCreateForm
              data={data}
              usuario={usuario}
              clinica={clinicaData}
              newClinicaId={newClinicaId}
            />
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute(
  "/settings/payments/create-recipient/add-bank-account",
)({
  component: AddBankAccountPage,
  pendingComponent: () => <PendingComponent title="Adicionar conta" />,
  validateSearch: (search) => addBankAccountPageSearchParamsSchema.parse(search),
});
